import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { partnerBySlug } from "@/data/partners";
import { PartnerCTA, PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { WhyYouNeedIt, HowItWorks } from "@/components/site/BusinessSetupBlocks";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import heroLlc from "@/assets/hero/hero-llc.jpg.asset.json";

const HERO = {
  src: heroLlc.url,
  alt: "LLC formation documents on a desk with a gold house model and keys, with a brick rental building in the background",
};

export const Route = createFileRoute("/business-setup/llc")({
  head: () =>
    buildHead({
      title: "Set Up an LLC for Investment Property | Simply Approved",
      description:
        "Form the LLC your business-purpose investment property loan will be vested in — operating agreement, EIN, registered agent, and state filing basics.",
      url: "/business-setup/llc",
      type: "article",
      keywords:
        "LLC for investment property, form an LLC for rental property, entity vesting mortgage, operating agreement for rental LLC, EIN for real estate LLC, registered agent for investment LLC, articles of organization rental property, LLC for DSCR loan, LLC for fix and flip loan, LLC formation for investors, certificate of good standing LLC",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
        { name: "Set up your LLC", url: "/business-setup/llc" },
      ],
      image: HERO.src,
      imageAlt: HERO.alt,
      service: { name: "LLC Formation for Investment Property" },
    }),
  component: LlcPage,
});

function LlcPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="Set up your LLC for investment property"
        subtitle="Our business-purpose loans are vested in an entity. Forming the LLC early keeps title, insurance, and loan documents aligned at closing."
        image={HERO}
      />
      <PageEssentials />
      <WhyYouNeedIt
        service={"an LLC for investment property"}
        intro={"We lend on business purpose only, and those loans are vested in an entity \u2014 not in your personal name. Forming the LLC before you go under contract keeps the purchase agreement, title commitment, insurance policy, and loan documents all reading the same name, which is what a clean closing depends on."}
        reasons={[
          { title: "Our loans require entity vesting", body: "DSCR, bridge, fix and flip, and portfolio loans close in an LLC, LP, or corporation. If the entity does not exist yet, the file cannot move to closing." },
          { title: "Liability separation on rental property", body: "Holding rentals in an entity is the standard way investors separate property risk from personal assets. Speak with your own attorney about your situation." },
          { title: "Cleaner ownership when partners are involved", body: "An operating agreement documents members and percentages up front, so guarantor structure and signing authority are never in question at closing." },
          { title: "It starts the entity's own credit identity", body: "Your EIN, business bank account, DUNS file, and vendor accounts all attach to the entity once it exists." },
        ]}
      />
      <Section>
        <SectionHeader eyebrow="What underwriting looks for" title="Entity documents we typically request" />
        <ul className="mt-6 grid gap-3 text-sm md:grid-cols-2">
          {[
            "Articles of Organization / Certificate of Formation filed with the state",
            "Operating Agreement showing members and ownership percentages",
            "EIN confirmation letter from the IRS",
            "Certificate of Good Standing (in some states)",
            "Registered agent on file in the property state",
            "Entity name matching title, insurance, and the loan documents exactly",
          ].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card p-4">{i}</li>
          ))}
        </ul>
        <HowItWorks
          steps={[
            "Choose the state, name, and members, then file the Articles of Organization.",
            "Get the EIN and sign an operating agreement listing members and percentages.",
            "Send us the formation documents so title, insurance, and the loan can be issued in the entity name.",
          ]}
        />
        <PartnerCTA partner={partnerBySlug("doola")!} />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply" className="rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold text-primary-foreground">Start My Approval</Link>
          <Link to="/partners" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">All investor partners</Link>
        </div>
        <PartnerDisclosure />
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
